import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import * as authApi from '../services/authApi';
import { setUnauthorizedHandler } from '../services/httpClient';

export const AuthContext = createContext(null);

// Cross-tab sync (storage fires in every OTHER tab, never the one that
// wrote the key): a tab that logs out or hits an unrecoverable 401
// broadcasts it here so sibling tabs drop their session immediately
// instead of showing stale "logged in" UI until their own next request.
const AUTH_BROADCAST_KEY = 'copilot:auth-event';

function broadcastAuthEvent(type) {
  try {
    window.localStorage.setItem(
      AUTH_BROADCAST_KEY,
      JSON.stringify({ type, ts: Date.now() })
    );
  } catch (_) {
    // Storage unavailable (private mode, quota, etc.) — cross-tab sync
    // degrades gracefully to "each tab notices on its own next request".
  }
}

// How often a logged-in tab silently re-checks its session in the
// background, so an expired/revoked refresh token is caught proactively
// rather than only on the user's next click. Well under the shortest
// possible refresh-token lifetime (REFRESH_TOKEN_SESSION_ONLY_HOURS).
const SESSION_HEARTBEAT_MS = 5 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionExpired, setSessionExpired] = useState(false);
  // True when the first session check failed because the API could not be
  // REACHED (network down, a gateway error, a refresh that could not be
  // answered) - which says nothing about whether the session is valid. The
  // route guard then offers a retry instead of sending a possibly signed-in
  // user to the login page.
  const [connectionProblem, setConnectionProblem] = useState(false);
  const manualLogoutRef = useRef(false);
  // False until a session is actually established in THIS tab (hydrate
  // succeeds, or login/register/refresh). A brand-new visitor with no
  // cookies at all fails hydrate() on the very first load — that is not
  // an expired session, just the absence of one, so the unauthorized
  // handler must stay quiet until a real session has existed.
  const hadSessionRef = useRef(false);

  const hydrate = useCallback(async () => {
    try {
      // If the access cookie is expired but the refresh cookie is still
      // valid, httpClient's refresh-and-retry logic transparently
      // refreshes before this resolves — sessions survive a closed tab.
      const me = await authApi.fetchMe();
      manualLogoutRef.current = false;
      hadSessionRef.current = true;
      setConnectionProblem(false);
      setUser(me);
    } catch (error) {
      setConnectionProblem(Boolean(error && error.isNetworkError));
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const retryConnection = useCallback(() => {
    setIsLoading(true);
    return hydrate();
  }, [hydrate]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    // Fires when a request's 401 could not be resolved by refreshing
    // (refresh token itself expired/invalid/revoked). Clearing user
    // here is what makes ProtectedRoute redirect to /login on its next
    // render — no imperative navigation needed.
    setUnauthorizedHandler(() => {
      setUser(null);

      const expired = hadSessionRef.current && !manualLogoutRef.current;
      setSessionExpired(expired);

      if (expired) {
        broadcastAuthEvent('session-expired');
      }
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    // Cross-tab sync: react to a logout or session-expiry that happened
    // in ANOTHER tab, instead of waiting for this tab's own next
    // request to independently discover the same thing.
    function onStorage(event) {
      if (event.key !== AUTH_BROADCAST_KEY || !event.newValue) {
        return;
      }

      let payload;
      try {
        payload = JSON.parse(event.newValue);
      } catch (_) {
        return;
      }

      if (payload.type === 'logout') {
        manualLogoutRef.current = true;
        setUser(null);
        setSessionExpired(false);
      } else if (payload.type === 'session-expired') {
        hadSessionRef.current = true;
        setUser(null);
        setSessionExpired(true);
      }
    }

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    // Proactive re-check while a session is active, so an expired or
    // server-revoked refresh token surfaces the "session expired"
    // state within one heartbeat instead of only on the next time the
    // user happens to trigger an API call.
    if (!user) {
      return undefined;
    }

    const interval = setInterval(() => {
      authApi.fetchMe().catch(() => {
        // A failure here is handled entirely by the unauthorized
        // handler above (it's the same 401 → refresh → give-up path as
        // any other request) — nothing further to do on this promise.
      });
    }, SESSION_HEARTBEAT_MS);

    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback(async (credentials) => {
    setError('');
    const me = await authApi.login(credentials);
    manualLogoutRef.current = false;
    hadSessionRef.current = true;
    setUser(me);
    setSessionExpired(false);
    return me;
  }, []);

  const register = useCallback(async (payload) => {
    setError('');
    const me = await authApi.register(payload);
    manualLogoutRef.current = false;
    hadSessionRef.current = true;
    setUser(me);
    setSessionExpired(false);
    return me;
  }, []);

  // Mirrors login() exactly - a Google session is an ordinary session,
  // so the same state transitions apply and nothing downstream needs to
  // know how it started.
  const loginWithGoogle = useCallback(async (credential) => {
    setError('');
    const me = await authApi.googleSignIn({ credential });
    manualLogoutRef.current = false;
    hadSessionRef.current = true;
    setUser(me);
    setSessionExpired(false);
    return me;
  }, []);

  // Verification returns the account AND sets the auth cookies, so it
  // is a session-establishing call like login - same state transitions.
  const verifyEmail = useCallback(async ({ email, code }) => {
    setError('');
    const me = await authApi.verifyEmail({ email, code });
    manualLogoutRef.current = false;
    hadSessionRef.current = true;
    setUser(me);
    setSessionExpired(false);
    return me;
  }, []);

  // Deliberately does NOT touch session state: no session exists yet,
  // and a failure here is about mail delivery, not authentication.
  const resendVerification = useCallback(
    ({ email }) => authApi.resendVerification({ email }),
    []
  );

  const logout = useCallback(async () => {
    manualLogoutRef.current = true;
    setSessionExpired(false);
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setSessionExpired(false);
      broadcastAuthEvent('logout');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await authApi.fetchMe();
    hadSessionRef.current = true;
    setUser(me);
    return me;
  }, []);

  const value = {
    user,
    isLoading,
    error,
    setError,
    sessionExpired,
    connectionProblem,
    retryConnection,
    clearSessionExpired: () => setSessionExpired(false),
    login,
    loginWithGoogle,
    verifyEmail,
    resendVerification,
    register,
    logout,
    refreshUser,
    updateUser: setUser,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
